import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Notice, Spinner } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { relativeDate } from '../../lib/school';
import '../Page.css';
import '../../components/Form.css';
import './Admin.css';

interface Health {
  version: string | null;
  env: string;
  emailEnabled: boolean;
  lastWeek: Record<string, number>;
  feedbackSeconds: { average: number | null; longest: number | null };
  stuck: number;
  failed: { id: string; created_at: string; duration_seconds: number | null; has_audio: boolean; teacher_name: string; school_name: string | null }[];
  totals: { schools: number; personal?: number; users: number; lessons: number; no_org_week?: number };
  frameworksAwaitingReview: number;
  /** Absent on older APIs. */
  transcription?: Transcription;
}
interface TranscriptionWindow {
  lessons: number;
  byProvider: Record<string, number>;
  fallbacks: number;
  audioSeconds: number;
}
interface Transcription {
  primary: string;
  hasFallback: boolean;
  last7: TranscriptionWindow;
  last30: TranscriptionWindow;
  reasons: { reason: string; count: number }[];
  lastDay: { primarySeconds: number; busiestHourSeconds: number };
  limits: { dailySeconds: number | null; hourlySeconds: number | null } | null;
  fallbackExtraCostUsd30: number;
}
interface AuditRow {
  id: string;
  action: string;
  target_type: string;
  target_id: string | null;
  detail: unknown;
  created_at: string;
  admin_name: string | null;
}

const minutes = (s: number | null) => (s == null ? '—' : s < 90 ? `${s}s` : `${Math.round(s / 60)} min`);

export function AdminHealthPage() {
  const qc = useQueryClient();
  const health = useQuery({ queryKey: ['admin-health'], queryFn: () => api<Health>('/admin/health'), refetchInterval: 60_000 });
  const audit = useQuery({ queryKey: ['admin-audit'], queryFn: async () => (await api<{ data: AuditRow[] }>('/admin/audit')).data });
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const retry = async (id: string) => {
    setMessage(null);
    try {
      await api(`/admin/sessions/${id}/retry`, { method: 'POST', body: '{}' });
      setMessage({ tone: 'success', text: 'Retry started. It takes a few minutes; refresh to see the result.' });
      await qc.invalidateQueries({ queryKey: ['admin-health'] });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'Couldn’t start a retry.' });
    }
  };

  if (health.isLoading) return <Spinner />;
  if (health.isError || !health.data) return <Notice tone="error">Couldn&rsquo;t load platform health.</Notice>;
  const h = health.data;
  const week = h.lastWeek;
  const recorded = Object.values(week).reduce((a, b) => a + b, 0);

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">EducAitor admin</p>
        <h1>Platform health</h1>
        <p className="muted">
          {h.env} · {h.version ?? 'unknown version'} · email {h.emailEnabled ? 'on' : 'off'}. Updates every minute.
        </p>
      </header>
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}

      <section className="page">
        <h2>Last 7 days</h2>
        <p className="muted">
          Every account, including EducAitor admins
          {Number(h.totals.no_org_week) ? ` and ${Number(h.totals.no_org_week)} from people not in any organisation` : ''}. Organisations shows all-time figures per organisation.
        </p>
        <div className="tiles">
          <Tile value={recorded} label="Lessons recorded" />
          <Tile value={week.completed ?? 0} label="Reports written" />
          <Tile value={minutes(h.feedbackSeconds.average)} label="Average time to feedback" />
          <Tile value={week.insufficient_audio ?? 0} label="Not enough audio" />
          <Tile value={week.failed ?? 0} label="Failed" tone={(week.failed ?? 0) > 0 ? 'bad' : undefined} />
          <Tile value={h.stuck} label="Stuck over an hour" tone={h.stuck > 0 ? 'warn' : undefined} />
        </div>
      </section>

      {h.transcription ? <TranscriptionSection t={h.transcription} /> : null}

      <section className="page">
        <h2>Overall</h2>
        <div className="tiles">
          <Tile value={Number(h.totals.schools)} label="Organisations" />
          {h.totals.personal != null ? <Tile value={Number(h.totals.personal)} label="Personal accounts" /> : null}
          <Tile value={Number(h.totals.users)} label="People" />
          <Tile value={Number(h.totals.lessons)} label="Lessons with reports" />
          <Tile value={h.frameworksAwaitingReview} label="Frameworks to review" tone={h.frameworksAwaitingReview > 0 ? 'warn' : undefined} />
        </div>
      </section>

      <LoopSection />

      <section className="page">
        <h2>Failed lessons (14 days)</h2>
        {h.failed.length === 0 ? (
          <div className="panel muted">None. Every lesson in the last two weeks was processed.</div>
        ) : (
          <div className="panel table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Teacher</th>
                  <th>School</th>
                  <th className="num">Length</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {h.failed.map((f) => (
                  <tr key={f.id}>
                    <td>{relativeDate(f.created_at)}</td>
                    <td>{f.teacher_name}</td>
                    <td>{f.school_name ?? '—'}</td>
                    <td className="num">{f.duration_seconds ? `${Math.round(f.duration_seconds / 60)} min` : '—'}</td>
                    <td className="num">
                      {f.has_audio ? (
                        <Button variant="ghost" onClick={() => retry(f.id)}>
                          Retry
                        </Button>
                      ) : (
                        <span className="faint">Audio deleted</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="page">
        <h2>Admin activity</h2>
        <div className="panel table-wrap">
          {(audit.data ?? []).length === 0 ? (
            <p className="muted">No admin changes yet.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {(audit.data ?? []).map((a) => (
                  <tr key={a.id}>
                    <td>{new Date(a.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                    <td>{a.admin_name ?? '—'}</td>
                    <td>{a.action.replace(/_/g, ' ')}</td>
                    <td className="audit-detail">{a.detail ? (typeof a.detail === 'string' ? a.detail : JSON.stringify(a.detail)) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

const NAMES: Record<string, string> = { groq: 'Groq', openai: 'OpenAI', mixed: 'Both' };
const hours = (s: number) => (s < 3600 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);
/** Used against a limit, in hours: "4.2 of 8 h". */
const ofHours = (used: number, limit: number) => `${(used / 3600).toFixed(1)} of ${Math.round((limit / 3600) * 10) / 10} h`;
const share = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '0%');

/**
 * How lessons are being transcribed: the primary service (Groq), how often
 * the OpenAI fallback stepped in and why, and how close the primary's plan
 * limits are over the last day. Counts and durations only.
 */
function TranscriptionSection({ t }: { t: Transcription }) {
  const primary = NAMES[t.primary] ?? t.primary;
  const w = t.last7;
  const fallbackShare = w.lessons ? w.fallbacks / w.lessons : 0;
  const day = t.limits?.dailySeconds ? t.lastDay.primarySeconds / t.limits.dailySeconds : null;
  const hour = t.limits?.hourlySeconds ? t.lastDay.busiestHourSeconds / t.limits.hourlySeconds : null;
  const tone = (r: number | null) => (r == null ? undefined : r >= 1 ? 'bad' : r >= 0.75 ? 'warn' : undefined);
  return (
    <section className="page">
      <h2>Transcription</h2>
      <p className="muted">
        Lessons are transcribed by {primary}
        {t.hasFallback && t.primary !== 'openai' ? ', with OpenAI stepping in if it refuses (for example over its plan limits)' : ''}. Last 7 days, by lesson.
      </p>
      <div className="tiles">
        <Tile value={w.byProvider[t.primary] ?? 0} label={`By ${primary}`} />
        {t.primary !== 'openai' ? (
          <Tile value={`${w.fallbacks} (${share(w.fallbacks, w.lessons)})`} label="OpenAI fallback" tone={fallbackShare >= 0.2 ? 'bad' : fallbackShare > 0 ? 'warn' : undefined} />
        ) : null}
        <Tile value={hours(w.audioSeconds)} label="Audio transcribed" />
        {day != null ? (
          <Tile value={ofHours(t.lastDay.primarySeconds, t.limits!.dailySeconds!)} label={`${primary} plan, last 24 hours`} tone={tone(day)} />
        ) : null}
        {hour != null ? (
          <Tile
            value={ofHours(t.lastDay.busiestHourSeconds, t.limits!.hourlySeconds!)}
            label={`${primary} plan, busiest hour`}
            tone={tone(hour)}
          />
        ) : null}
      </div>
      {t.last30.fallbacks ? (
        <div className="panel health-note">
          <p>
            <strong>
              {t.last30.fallbacks} of {t.last30.lessons} lessons
            </strong>{' '}
            in the last 30 days went to OpenAI instead
            {t.fallbackExtraCostUsd30 ? `, about $${t.fallbackExtraCostUsd30.toFixed(2)} more than ${primary} would have cost` : ''}.
            {t.reasons.length ? ` Why: ${t.reasons.map((r) => `${r.reason} × ${r.count}`).join(', ')}.` : ''}
          </p>
          {t.reasons.some((r) => r.reason.includes('429')) ? (
            <p className="muted">Rate limits mean {primary}&rsquo;s plan is too small for the volume. A paid plan raises the limits.</p>
          ) : null}
        </div>
      ) : w.lessons ? (
        <p className="faint">No fallbacks in the last 30 days.</p>
      ) : (
        <p className="faint">No lessons transcribed in the last 7 days yet. Figures start from this release.</p>
      )}
    </section>
  );
}

function Tile({ value, label, tone }: { value: number | string; label: string; tone?: 'warn' | 'bad' }) {
  return (
    <div className={`tile${tone ? ` tile-${tone}` : ''}`}>
      <span className="tile-value">{value}</span>
      <span className="tile-label">{label}</span>
    </div>
  );
}

interface Loop {
  days: number;
  teachersRecording: number;
  teachersChoseFocus: number;
  activeFocuses: number;
  focusesFinished: number;
  lessons: number;
  lessonsWithFocus: number;
  checkIns: Partial<Record<'yes' | 'partly' | 'not_yet' | 'not_this_lesson' | 'unanswered', number>>;
  focusScore: { measured: number; improved: number };
  ratings: Partial<Record<'up' | 'down', number>>;
  downReasons: Record<string, number>;
  mostFlagged: { indicator: string; n: number }[];
  coach: { messages: number; people: number };
  coachReports: Partial<Record<'harmful' | 'wrong' | 'other', number>>;
}

const REASON_LABEL: Record<string, string> = {
  not_accurate: 'Not accurate',
  too_generic: 'Too generic',
  missed_context: 'Missed the context',
  too_long: 'Too long',
  other: 'Other',
};

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

/** Is closing the loop working? Counts only, never lesson content. */
function LoopSection() {
  const [days, setDays] = useState(30);
  const loop = useQuery({ queryKey: ['admin-loop', days], queryFn: () => api<Loop>(`/admin/loop?days=${days}`) });
  const l = loop.data;
  const tried = (l?.checkIns.yes ?? 0) + (l?.checkIns.partly ?? 0);
  const answered = tried + (l?.checkIns.not_yet ?? 0) + (l?.checkIns.not_this_lesson ?? 0);
  const rated = (l?.ratings.up ?? 0) + (l?.ratings.down ?? 0);
  const reasons = Object.entries(l?.downReasons ?? {}).sort((a, b) => b[1] - a[1]);
  const coachReported = Object.values(l?.coachReports ?? {}).reduce((a, b) => a + (b ?? 0), 0);

  return (
    <section className="page">
      <div className="admin-head">
        <div>
          <h2>Closing the loop</h2>
          <p className="muted">Teachers choosing one thing to try, trying it, and whether that indicator&rsquo;s score goes up. EducAitor admins excluded.</p>
        </div>
        <div className="chips" role="group" aria-label="Period">
          {[7, 30, 90].map((d) => (
            <button key={d} type="button" className="choice-chip" aria-pressed={days === d} onClick={() => setDays(d)}>
              {d} days
            </button>
          ))}
        </div>
      </div>
      {loop.isLoading ? (
        <Spinner />
      ) : loop.isError || !l ? (
        <Notice tone="error">Couldn&rsquo;t load these figures.</Notice>
      ) : (
        <>
          <div className="tiles">
            <Tile value={pct(l.teachersChoseFocus, l.teachersRecording)} label={`of teachers recording chose a focus (${l.teachersChoseFocus} of ${l.teachersRecording})`} />
            <Tile value={pct(tried, answered)} label={`of check-ins said they tried it (${tried} of ${answered})`} />
            <Tile value={pct(l.focusScore.improved, l.focusScore.measured)} label={`of focuses saw their score go up (${l.focusScore.improved} of ${l.focusScore.measured})`} />
            <Tile value={l.focusesFinished} label="Focuses finished" />
            <Tile value={l.activeFocuses} label="Focuses in progress now" />
            <Tile value={pct(l.lessonsWithFocus, l.lessons)} label="of lessons recorded with a focus" />
          </div>
          <div className="tiles">
            <Tile value={pct(l.ratings.up ?? 0, rated)} label={`of rated reports were useful (${rated} rated)`} tone={rated && (l.ratings.down ?? 0) > (l.ratings.up ?? 0) ? 'warn' : undefined} />
            <Tile value={l.coach.messages} label={`AI coach messages, from ${l.coach.people} ${l.coach.people === 1 ? 'person' : 'people'}`} />
            <Tile
              value={coachReported}
              label={`AI coach replies reported${coachReported ? ` (${l.coachReports.harmful ?? 0} harmful, ${l.coachReports.wrong ?? 0} wrong, ${l.coachReports.other ?? 0} other)` : ''}. Each one is emailed to support.`}
              tone={l.coachReports.harmful ? 'warn' : undefined}
            />
          </div>
          {reasons.length || l.mostFlagged.length ? (
            <div className="panel loop-detail">
              {reasons.length ? (
                <div>
                  <h3>Why reports weren&rsquo;t useful</h3>
                  <ul>
                    {reasons.map(([k, n]) => (
                      <li key={k}>
                        {REASON_LABEL[k] ?? k} <span className="faint">· {n}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {l.mostFlagged.length ? (
                <div>
                  <h3>Most flagged feedback</h3>
                  <ul>
                    {l.mostFlagged.map((f) => (
                      <li key={f.indicator}>
                        {f.indicator} <span className="faint">· {f.n}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
